import { useMemo, useState } from 'react';
import {
  applySmartFilter, buildAlerts, deadlineInfo, fmtCcy, priorityScore,
  SMART_FILTERS, toPipelineRows, type SmartFilterId
} from '../domain';
import type { Assignment, Customer, LogEntry, NewBizProspect, User } from '../types';
import {
  AccountTypeBadge, AlertBanner, OutcomeBadge, PipelineBadge, PriorityBadge
} from './Badges';

interface Props {
  user: User;
  customers: Customer[];
  assignments: Assignment[];
  newbiz: NewBizProspect[];
  logs: LogEntry[];
  onOpenNewBiz: () => void;
  onAutoAssignAll: () => void;
  onUpdate: (kind: 'assign' | 'newbiz', id: string) => void;
  onReassign: (kind: 'assign' | 'newbiz', id: string, currentAm: string) => void;
  onDelete: (kind: 'assign' | 'newbiz', id: string) => void;
}

export function MyPipeline(props: Props) {
  const { user, customers, assignments, newbiz, logs,
    onOpenNewBiz, onAutoAssignAll, onUpdate, onReassign, onDelete } = props;

  const [filter, setFilter] = useState<SmartFilterId>('all');
  const isAdmin = user.role === 'admin';

  const visibleAssigns = isAdmin ? assignments : assignments.filter(a => a.amName === user.name);
  const visibleNb = isAdmin ? newbiz : newbiz.filter(n => n.amName === user.name);
  const rows = useMemo(() => toPipelineRows(visibleAssigns, visibleNb), [visibleAssigns, visibleNb]);
  const filtered = useMemo(
    () => applySmartFilter(filter, rows, customers, logs, user.name),
    [filter, rows, customers, logs, user.name]
  );

  const alerts = buildAlerts(isAdmin ? null : user.name, assignments, newbiz, logs);

  return (
    <>
      <div className="section-hdr">
        <div className="section-hdr-title">Account <em style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Pipeline</em></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {isAdmin && (
            <button className="action-btn btn-call" onClick={onAutoAssignAll}>⚡ Auto-Assign All</button>
          )}
          <button className="action-btn btn-note" onClick={onOpenNewBiz}>+ New Prospect</button>
        </div>
      </div>

      <div className="filter-chips" style={{ marginBottom: 14 }}>
        {SMART_FILTERS.map(f => (
          <button
            key={f.id}
            className={`chip ${filter === f.id ? 'on' : ''}`}
            onClick={() => setFilter(f.id)}
          >{f.label}</button>
        ))}
      </div>

      {alerts.map((a, i) => <AlertBanner key={i} type={a.type} msg={a.msg} />)}

      <div className="mgmt-section">
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
          {filtered.length} account{filtered.length === 1 ? '' : 's'} matching filter
        </div>
        {filtered.length === 0 ? (
          <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>No accounts match this filter.</p>
        ) : (
          filtered.map(a => {
            const cust = a.custId ? customers.find(c => c.id === a.custId) : null;
            const dl = deadlineInfo(a.deadline);
            const lastTs = cust ? Math.max(0, ...logs.filter(l => l.customerId === cust.id).map(l => l.ts)) : 0;
            const pri = cust ? priorityScore(cust, lastTs) : null;
            return (
              <div key={a.id} className="assign-row">
                <div className="assign-name" style={{ minWidth: 160 }}>
                  <div style={{ fontWeight: 500 }}>{a.custName || '—'}</div>
                  {isAdmin && <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{a.amName}</div>}
                </div>
                <AccountTypeBadge type={a.source === 'newbiz' ? 'newbiz' : a.accountType} />
                <PipelineBadge status={a.status} />
                <OutcomeBadge outcome={a.outcome} />
                {pri && <PriorityBadge level={pri} />}
                <div className={dl.cls} style={{ fontSize: 10, whiteSpace: 'nowrap' }}>{dl.label}</div>
                {a.revenue ? <div style={{ fontSize: 11, color: 'var(--accent)' }}>{fmtCcy(a.revenue)}</div> : null}
                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                  <button className="entry-del-btn" onClick={() => onUpdate(a.source, a.id)}>Update</button>
                  {isAdmin && (
                    <>
                      <button className="entry-del-btn" onClick={() => onReassign(a.source, a.id, a.amName)}>Reassign</button>
                      <button className="entry-del-btn" onClick={() => onDelete(a.source, a.id)}>✕</button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
